import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
    CardsNgrxFacade,
    CustomerCard,
    CustomerCardType,
    FavoritePreference,
    NotificationFacade,
    PurchaseService,
    SessionService,
    ShoppingCartService
} from '@ridango/components';
import { filter, finalize, map, take, takeUntil } from 'rxjs/operators';
import { AuthService, NavigationService, TravelMedia } from '@ridango/core';
import Env from '@ridango/core/env';
import { PurchaseStep } from '../models/purchase-step.model';
import { Router } from '@angular/router';
import {
    ActivationMode,
    ActivationType,
    Area,
    AreaCode,
    CartTicketRequest,
    Medium,
    MediumCode,
    NextButtonElement,
    Preferences,
    Product,
    ProductArea,
    ProductAreaType,
    Ticket,
    URLData
} from '@ridango/components/lib/purchase/purchase.model';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TicketPurchaseSameValidityConfirmationComponent } from '../ticket-purchase-same-validity-confirmation/ticket-purchase-same-validity-confirmation.component';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { Location } from '@angular/common';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { HelperService } from '../../../shared/services/helper.service';
import { ProductCategoryAreaNameEnum } from '@ridango/core/lib/enums/product-category-area-name.enum';
import { ModalController } from '@ionic/angular';
import { Temporal } from '@js-temporal/polyfill';
import { CardsPurchaseFacadeService } from '@ridango/widgets';
import { isDynamicBookletProduct } from './purchase-page-v2-booklet.util';

interface TicketDTO extends Pick<Product, 'productId' | 'startZoneId' | 'endZoneId' | 'dynamicProductParameters'> {
    email: string;
    units?: number;
}

@Injectable({ providedIn: 'root' })
export class PurchasePageV2FacadeService implements OnDestroy {
    private readonly stepsDescription: string[] = this.env.PURCHASE_FLOW.TICKET_STEPS_DESCRIPTION ?? [];
    private readonly productsLocalStorageKey = this.env.APP_NAME + '-products-to-purchase';
    private readonly NEXT = 'Next';
    private readonly stepsNames: string[] = this.env.PURCHASE_FLOW.TICKET_STEPS;
    private readonly _steps: PurchaseStep[] = [...this.stepsNames].map((step, index) => ({
        name: step,
        description: this.stepsDescription[index]
    }));
    private readonly defaultDataFromUrl: URLData = {
        selectedPreferenceId: null,
        selectedAreaId: null,
        selectedProductAreas: null,
        selectedProductId: null,
        ticketsFromUrl: null
    };

    private isCartCreationInProgress = false;
    private activatedAt: string;
    private allProducts: Product[] = [];
    private selectedActivationMethod: ActivationType;

    private _selectedArea$ = new BehaviorSubject<Area | undefined>(undefined);
    private _savedPreference$ = new BehaviorSubject<FavoritePreference | undefined>(undefined);
    private _productAreas$ = new BehaviorSubject<ProductArea[]>([]);
    private _displaySteps$ = new BehaviorSubject<PurchaseStep[]>([]);

    readonly isNewAfc = this.env.IS_NEW_AFC;

    stepFromUrl$ = new BehaviorSubject<string | undefined>(undefined);
    selectedPreference$ = new BehaviorSubject<Preferences | undefined>(undefined);
    dataFromUrl$ = new BehaviorSubject<URLData>(this.defaultDataFromUrl);
    componentDestroyed$ = new Subject<void>();
    isBackButtonHidden$ = new BehaviorSubject<boolean>(true);
    preferences$ = new BehaviorSubject<Preferences[]>([]);
    nextButton$ = new BehaviorSubject<NextButtonElement | null>(null);
    areas$ = new BehaviorSubject<Area[]>([]);

    selectedArea$ = this._selectedArea$.asObservable();
    savedPreference$ = this._savedPreference$.asObservable();
    productAreas$ = this._productAreas$.asObservable();
    displaySteps$ = this._displaySteps$.asObservable();

    steps: PurchaseStep[] = this._steps;
    nextButton: NextButtonElement;
    activeStep = 0;
    isPurchaseByCard = false;
    isAddOneMoreTicket = false;

    constructor(
        @Inject('ENV') private readonly env: Env,
        private router: Router,
        private purchaseService: PurchaseService,
        private translate: TranslateService,
        private analyticsService: AnalyticsService,
        private location: Location,
        private shoppingCartService: ShoppingCartService,
        private sessionService: SessionService,
        private helperService: HelperService,
        private notification: NotificationFacade,
        private authService: AuthService,
        private cardsFacade: CardsNgrxFacade,
        private modalCtrl: ModalController,
        private cardsPurchaseFacadeService: CardsPurchaseFacadeService,
        private navigationService: NavigationService
    ) {}

    ngOnDestroy(): void {
        this.initDestroy();
    }

    initSteps(): void {
        const isCard = this.cardsPurchaseFacadeService.isPurchaseByCard;
        this.steps = this.getSteps(isCard);
        this._displaySteps$.next(this.steps);
        this.setIsPurchaseFromCard(isCard);
    }

    getCardMedium$(): Observable<Medium> {
        return this.purchaseService
            .getMediums()
            .pipe(map((mediums) => mediums.find((m) => m.code === TravelMedia.card)));
    }

    setSelectedActivationMethod(activationMethod: ActivationType): void {
        this.selectedActivationMethod = activationMethod;
    }

    setIsAddOneMoreTicket(addOneMoreTicket: boolean): void {
        this.isAddOneMoreTicket = addOneMoreTicket;
    }

    setBackButtonVisibility(isHidden: boolean): void {
        this.isBackButtonHidden$.next(isHidden);
    }

    setProductAreas(productAreas: ProductArea[]): void {
        this._productAreas$.next(productAreas);
    }

    setSelectedArea(area: Area | undefined): void {
        this._selectedArea$.next(area);
    }

    setNextButton(nextButton: NextButtonElement | null): void {
        queueMicrotask(() => {
            this.nextButton$.next(nextButton);
        });
    }

    setAreas(areas: Area[]): void {
        this.areas$.next(areas);
    }

    setDataFromUrl(data: URLData): void {
        this.dataFromUrl$.next(data);
    }

    setStepFromUrl(step: string): void {
        this.stepFromUrl$.next(step);
    }

    setActiveStep(stepIndex: number): void {
        this.activeStep = stepIndex;
    }

    setSavedPreference(savedPreference: FavoritePreference): void {
        this._savedPreference$.next(savedPreference);
    }

    handleNextClicked(urlSegments: string[], _step?: string): void {
        const step = _step || this.stepFromUrl$.value;

        if (step === 'product' && this.purchaseService.isInDateSelectionMode()) {
            this.purchaseService.triggerDateSelectionConfirmation();
            return;
        }

        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            label: this.NEXT
        });
        this.purchaseService.setPurchaseDataLoading(true);
        if (step === 'areas') {
            this.initiateAreas();
        }
        if (step === 'ticket-activation') {
            this.purchaseService.emitActivationUrl(
                this.location.path().replace(ActivationType.AFTER_PURCHASE, 'AFTER_PURCHASE')
            );
        }
        if (this.steps.findIndex((s) => s.name === step) === this.steps.length - 2) {
            this.prePurchaseCheckTickets(() => {
                this.goToCart();
            });
            return;
        }
        const index = this.steps.findIndex((s) => s.name === step);
        const nextStep = this.steps[index + 1];

        urlSegments.push(nextStep.name);

        this.router.navigate([this.translate.currentLang, 'purchase-v2', ...urlSegments]);
    }

    createPreferenceByCard(card: CustomerCard): Preferences {
        const code = card.cardType === CustomerCardType.EMV ? TravelMedia.bank_card : TravelMedia.card;
        return <Preferences>{
            id: card.id,
            value: card.rawCardNumber,
            code: code,
            name: card.name,
            description: card.cardNumber,
            discountGroups: null
        };
    }

    initPreferences(): void {
        this.getPreferences()
            .pipe(
                takeUntil(this.componentDestroyed$),
                finalize(() => this.purchaseService.setPurchaseDataLoading(false))
            )
            .subscribe(
                (preferences) => {
                    if (this.preferences$.value.length === 0) {
                        this.setPreferences(preferences);
                    }

                    if (this.env.PURCHASE_FLOW?.TICKET_CONF?.PREFERENCES?.MEDIA_OPTIONS) {
                        this.initiateAreas();
                    }
                },
                (error) => {
                    console.error('Error', error);
                }
            );
    }

    onActivationModeChange(mode: ActivationMode): void {
        this.purchaseService.setActivationMode(mode);
    }

    onDateChange(selectedDate: Temporal.PlainDate): void {
        this.purchaseService.setStartingDate(selectedDate);
    }

    handleSelectedOptionChange(option): void {
        this.setSelectedPreferenceOption(option);
        this.isPurchaseByCard && this.purchaseService.choosePreference(option);

        if (option.code === 'BANK_CARD') {
            return;
        }

        const selectedArea = this.transformToSelectAreaData(option);

        this.setSelectedArea(selectedArea);

        if (selectedArea && selectedArea.id) {
            this.purchaseService.getProductAreas(selectedArea.id).subscribe(
                (productAreas) => {
                    this._productAreas$.next(productAreas);
                },
                (error) => {
                    console.error('Error fetching product areas:', error);
                }
            );
        }
    }

    bottomPartSize(isDesktop = false): string {
        const isMobile = this.env.IS_MOBILE_APP || !isDesktop;
        if (this.nextButton) {
            if (this.env.APP_NAME === 'varmland') {
                return isMobile ? '11.5625rem' : '21.5rem';
            }
            return isMobile ? '10.5rem' : '21.5rem';
        }
        if (this.env.APP_NAME === 'varmland') {
            return isMobile ? '7.0625rem' : '17rem';
        }
        return isMobile ? '6rem' : '17rem';
    }

    shouldShowCloseButton(): boolean {
        return (
            this.steps.findIndex((step) => step.name === this.stepFromUrl$.value) !== 0 &&
            !this.env.PURCHASE_FLOW?.HIDE_CLOSE_BUTTON
        );
    }

    handleErrorEvent(message?: string) {
        this.purchaseService.setPurchaseDataLoading(false);
        this.router.navigate([this.translate.currentLang, 'purchase-v2', this.steps[0].name]);
        const normalizedMessage = typeof message === 'string' && message.trim().length > 0 ? message : null;
        if (normalizedMessage) {
            this.notification.error(this.translate.instant(normalizedMessage));
        }
    }

    setSelectedPreferenceOption(option): void {
        this.selectedPreference$.next(option);
    }

    resetData(): void {
        this.setIsPurchaseFromCard(false);
        this.setActiveStep(0);
        this.setSelectedPreferenceOption(undefined);
        this.setProductAreas([]);
        this.setAreas([]);
        this.setPreferences([]);
        this.setSelectedArea(undefined);
        this.steps = this._steps;
        this.dataFromUrl$.next(this.defaultDataFromUrl);
        this.nextButton$.next(null);
        this.initDestroy();
    }

    private getSteps(isByCard: boolean): PurchaseStep[] {
        return isByCard ? [...this._steps].filter((step) => step.name !== 'preferences') : this._steps;
    }

    private setIsPurchaseFromCard(isFromCard: boolean): void {
        this.isPurchaseByCard = isFromCard;
    }

    private initDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    private setPreferences(preferences: Preferences[]): void {
        this.preferences$.next(preferences);
    }

    private getCurrentAreaValue() {
        return this.navigationService.getNextUrlSegment('product-area');
    }

    private goToCart() {
        // TODO: if user delete ticket in cart and go to previous step need to sync localStorage with route data;
        if (this.isCartCreationInProgress) {
            return;
        }
        const areaValue = this.getCurrentAreaValue();
        this.purchaseService.setPurchaseDataLoading(true);

        this.purchaseService
            .getProducts([areaValue])
            .pipe(take(1))
            .subscribe(
                (products) => {
                    this.allProducts = products;
                    this.resolveActivatedAtForCart();
                    const tickets = this.buildCartTickets(products);
                    this.submitTicketsToCart(this.shoppingCartService.getCartId(), tickets);
                },
                () => {
                    this.purchaseService.setPurchaseDataLoading(false);
                }
            );
    }

    private resolveActivatedAtForCart(): void {
        if (this.env.IS_NEW_AFC && this.purchaseService.getStepDataIdsByStepName('ticket-activation')[1]) {
            this.activatedAt = this.purchaseService.getStepDataIdsByStepName('ticket-activation')[1];
        }

        if (!this.env.ENABLE_CHOOSE_STARTING_DATE) {
            return;
        }

        const currentMode = this.purchaseService.getCurrentActivationMode();
        const currentSelectedDate = this.purchaseService.getCurrentStartingDate();

        if (currentMode === ActivationMode.SPECIFIC_DATE && currentSelectedDate) {
            this.activatedAt = currentSelectedDate.toString();
            return;
        }
        if (currentMode === ActivationMode.IMMEDIATELY) {
            this.activatedAt = null;
            return;
        }
        this.activatedAt = moment.utc().format();
    }

    private buildCartTickets(products: Product[]): CartTicketRequest[] {
        const localTickets = JSON.parse(localStorage.getItem(this.productsLocalStorageKey));
        if (localTickets && Array.isArray(localTickets) && localTickets.length) {
            return this.buildTicketsFromLocalStorage(products, localTickets);
        }

        const urlProductId = this.getUrlProductId();
        const productFromUrl = products.find((product) => product?.id === urlProductId);
        if (!productFromUrl) {
            return [];
        }

        return [this.buildTicketFromUrlProduct(productFromUrl)];
    }

    private buildTicketsFromLocalStorage(
        products: Product[],
        localTickets: Array<Product & { amount?: number; id?: string | number }>
    ): CartTicketRequest[] {
        const tickets: CartTicketRequest[] = [];
        localTickets.forEach((ticket) => {
            const amount = ticket.amount ?? 1;
            const freshProduct = products.find((p) => String(p.id) === String(ticket.id ?? ticket.productId));
            if (this.isBookletProduct(freshProduct)) {
                tickets.push(this.generateTicketPayload(ticket, amount));
                return;
            }
            for (let j = 0; j < amount; j++) {
                tickets.push(this.generateTicketPayload(ticket));
            }
        });
        return tickets;
    }

    private isBookletProduct(product: Product | undefined): boolean {
        return isDynamicBookletProduct(product);
    }

    private buildTicketFromUrlProduct(productFromUrl: Product): CartTicketRequest {
        const productIdStr = String(productFromUrl.id);
        const extractedDynamicParameters = this.getDynamicParamsFromUrl(productIdStr);
        const hasDynamicParameters = Object.keys(extractedDynamicParameters).length > 0;
        const productToProcess = hasDynamicParameters
            ? { ...productFromUrl, dynamicProductParameters: extractedDynamicParameters }
            : productFromUrl;
        return this.generateTicketPayload(productToProcess);
    }

    private submitTicketsToCart(cartId: number | null, tickets: CartTicketRequest[]): void {
        if (this.env.IS_NEW_AFC && cartId) {
            this.shoppingCartService.getShoppingCartStatus(cartId).subscribe((currentCart) => {
                currentCart.tickets.forEach((ticket: Ticket) => {
                    tickets.push({
                        productId: ticket.productId,
                        dynamicProductParameters: ticket.dynamicProductParameters || {},
                        parametersCode: ticket.parametersCode,
                        starting: ticket.starting
                    });
                });
                this.addProductToCart(cartId, tickets, true);
            });
            return;
        }
        this.addProductToCart(cartId || null, tickets, true);
    }

    private getDynamicParamsFromUrl(productId: string): { [key: string]: number } {
        const segments = this.navigationService.getAllUrlSegments();

        const params: { [key: string]: number } = {};
        segments.forEach((segment, index) => {
            if (segment.path.startsWith(`${productId}:`)) {
                const key = segment.path.split(':')[1];
                const value = segments[index + 1]?.path;

                if (key && value && !isNaN(Number(value))) {
                    params[key] = Number(value);
                }
            }
        });
        return params;
    }

    private generateTicketPayload(ticket: Product, units?: number): TicketDTO {
        const newTicket: TicketDTO = {
            productId: ticket?.id,
            email: this.sessionService.getLoggedInEmail(),
            startZoneId: ticket?.startZoneId,
            endZoneId: ticket?.endZoneId,
            dynamicProductParameters: ticket?.dynamicProductParameters || {}
        };

        this.applyUnitsToTicket(newTicket, units);
        this.applyDynamicLengthDatesToTicket(newTicket, ticket);
        this.applyZoneOrParametersCodeToTicket(newTicket, ticket);
        this.applyStartingDateToTicket(newTicket, ticket);
        this.applyPreferenceMediaToTicket(newTicket);
        return newTicket;
    }

    private applyUnitsToTicket(newTicket: TicketDTO, units?: number): void {
        if (units != null && units > 0) {
            newTicket.units = units;
        }
    }

    private formatTicketDate(date: Date | string): string {
        const parsedDate = date instanceof Date ? date : new Date(date);
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private applyDynamicLengthDatesToTicket(newTicket: TicketDTO, ticket: Product): void {
        if (!this.purchaseService.isDynamicLengthProduct(ticket)) {
            return;
        }
        if (ticket?.starting) {
            newTicket['starting'] = this.formatTicketDate(ticket.starting);
        }
        if (ticket?.ending) {
            newTicket['ending'] = this.formatTicketDate(ticket.ending);
        }
    }

    private applyZoneOrParametersCodeToTicket(newTicket: TicketDTO, ticket: Product): void {
        if (this.env.IS_NEW_AFC && ticket?.parametersCode) {
            newTicket['parametersCode'] = ticket.parametersCode;
            return;
        }
        newTicket['startZoneId'] = ticket?.startZoneId;
        newTicket['endZoneId'] = ticket?.endZoneId;
    }

    private applyStartingDateToTicket(newTicket: TicketDTO, ticket: Product): void {
        if (!this.env.ENABLE_CHOOSE_STARTING_DATE || this.purchaseService.isDynamicLengthProduct(ticket)) {
            return;
        }
        if (this.selectedActivationMethod === ActivationType.MANUAL) {
            newTicket['starting'] = null;
            return;
        }

        const currentMode = this.purchaseService.getCurrentActivationMode();
        const currentSelectedDate = this.purchaseService.getCurrentStartingDate();
        if (currentMode === ActivationMode.SPECIFIC_DATE && currentSelectedDate) {
            newTicket['starting'] = currentSelectedDate.toString();
            return;
        }

        const activationDateParams = this.purchaseService.getPrePurchaseCheckActivationDateParams(
            this.selectedActivationMethod
        );
        if (!activationDateParams.isToday && !this.activatedAt) {
            newTicket['starting'] = this.helperService
                .appDateTimeToServerDateTime(activationDateParams.date)
                .toString();
        }
        if (this.env.IS_NEW_AFC && this.activatedAt) {
            newTicket['starting'] = this.helperService.appDateToServerDateTime(this.activatedAt).toString();
        }
    }

    private applyPreferenceMediaToTicket(newTicket: TicketDTO): void {
        const selectedPreference = this.selectedPreference$.value;
        if (!selectedPreference) {
            return;
        }
        if (selectedPreference.code === TravelMedia.card || selectedPreference.code === TravelMedia.bank_card) {
            newTicket['cardId'] = selectedPreference.value;
        }
        if (selectedPreference.code === TravelMedia.email) {
            newTicket['email'] = selectedPreference.value;
        }
        if (selectedPreference.code === TravelMedia.personalCode) {
            newTicket['cardId'] = selectedPreference.value;
        }
    }

    private shouldBackfillTicketStarting(
        ticket: CartTicketRequest,
        activationDateParams: ReturnType<PurchaseService['getPrePurchaseCheckActivationDateParams']>
    ): boolean {
        if (this.selectedActivationMethod === ActivationType.MANUAL) {
            return false;
        }

        const ticketProduct = this.allProducts?.find((p) => p.id === ticket.productId);
        if (ticketProduct && this.purchaseService.isDynamicLengthProduct(ticketProduct) && ticket.starting) {
            return false;
        }

        if (this.env.IS_NEW_AFC) {
            return ticket.starting == null;
        }

        return !activationDateParams.isToday && !this.activatedAt;
    }

    private addProductToCart(shoppingCartId, tickets, retry: boolean) {
        if (this.isCartCreationInProgress) {
            return;
        }
        this.isCartCreationInProgress = true;

        const data: { shoppingCartId; tickets: CartTicketRequest[] } = { shoppingCartId, tickets };

        data.tickets = this.mapTicketsData(data.tickets);

        this.shoppingCartService
            .addProductToCartV2(data)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.onAddProductToCartSuccess();
                },
                (error) => {
                    this.onAddProductToCartError(error, tickets, retry);
                }
            );
    }

    private mapTicketsData(tickets: CartTicketRequest[]): CartTicketRequest[] {
        return tickets.map((ticket: CartTicketRequest) => {
            const ticketProduct = this.allProducts.find((product) => product.id === ticket.productId);

            if (!ticketProduct?.productParameters || (ticketProduct?.productParameters && !ticket.parametersCode)) {
                const activationDateParams = this.purchaseService.getPrePurchaseCheckActivationDateParams(
                    this.selectedActivationMethod
                );

                if (this.selectedActivationMethod === ActivationType.MANUAL) {
                    ticket.starting = null;
                }

                // Backfill `starting` only when allowed. Preserve per-ticket `starting` in NEW_AFC; apply legacy backfill rules otherwise.
                if (this.shouldBackfillTicketStarting(ticket, activationDateParams)) {
                    ticket.starting = this.helperService
                        .appDateTimeToServerDateTime(activationDateParams.date)
                        .toString();
                }
            }

            if (ticketProduct?.productParameters && !ticket.parametersCode) {
                const parametersCode = ticketProduct.productParameters?.availableParameters?.activations.find(
                    (el) => el.activationTrigger === this.selectedActivationMethod
                )?.parametersCode;

                return {
                    ...ticket,
                    parametersCode
                };
            }

            return ticket;
        });
    }

    private onAddProductToCartSuccess(): void {
        this.isCartCreationInProgress = false;
        this.purchaseService.resetProductsLocalStorage();
        this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart']);
    }

    private onAddProductToCartError(error: any, tickets, retry): void {
        this.isCartCreationInProgress = false;
        if (error.status === 409 && retry) {
            this.addProductToCart(null, tickets, false);
        } else {
            this.purchaseService.setPurchaseDataLoading(false);
            const errorMessage = error?.error?.message || error?.message || 'CART_LOADING_ERROR';
            this.notification.error(this.translate.instant(errorMessage));
        }
    }

    private handleSelectArea(area: Area) {
        const prevArea = this._selectedArea$.value;
        this.setSelectedArea(area);

        const selectedArea = area;
        const stepsHasArea = this.steps.some((step) => step.name.includes('areas'));

        if (selectedArea || !stepsHasArea) {
            if (selectedArea?.id !== prevArea?.id || !stepsHasArea) {
                const areas = this.purchaseService.getStepDataIdsByStepName('areas');
                if (
                    this.env.IS_NEW_AFC &&
                    areas[0] !==
                        `productCategory:${this.env.PRODUCT_CATEGORY_AREA_NAME || ProductCategoryAreaNameEnum.CITY_MUNI}`
                ) {
                    const areasRequests = area.subCategories.map((area) =>
                        this.purchaseService.getProductAreas(area.id)
                    );

                    combineLatest(areasRequests)
                        .pipe(take(1))
                        .subscribe(
                            ([oneArea, twoArea, country]) => {
                                const areaTypes = [
                                    ProductAreaType.ONE_AREA,
                                    ProductAreaType.TWO_AREAS,
                                    ProductAreaType.COUNTY
                                ];
                                const productAreas = [oneArea, twoArea, country];

                                this._productAreas$.next(this.assignProductAreaCodes(productAreas, areaTypes));
                            },
                            (error) => {
                                console.error('Error', error);
                            },
                            () => this.purchaseService.setPurchaseDataLoading(false)
                        );
                } else {
                    this._productAreas$.next([]);
                    this.purchaseService
                        .getProductAreas(selectedArea.id)
                        .pipe(take(1))
                        .subscribe(
                            (productAreas) => {
                                this._productAreas$.next(productAreas);
                            },
                            (error) => {
                                console.error('Error', error);
                            }
                        );
                }
            }
        } else {
            this._productAreas$.next([]);
        }
    }

    private getPreferences(): Observable<Preferences[]> {
        if (this.authService.getLoginStatus()) {
            if (!this.env.IS_NEW_AFC) {
                this.cardsFacade.fetchCustomerCards();
            }
            const userEmail = this.authService.getLoggedInEmail();
            return this.cardsFacade.customerCards$.pipe(
                filter((cards) => !!cards),
                map((cards) =>
                    cards.filter((card) => {
                        const hiddenEmvSchemes = this.env.PURCHASE?.HIDDEN_EMV_SCHEMES ?? ['VISA'];
                        return !hiddenEmvSchemes.includes(card.scheme);
                    })
                ),
                map((cards) => {
                    const responseMapped = cards.map((card) => {
                        return this.createPreferenceByCard(card);
                    });
                    if (userEmail) {
                        responseMapped.push({
                            id: null,
                            value: userEmail,
                            code: TravelMedia.email,
                            name: userEmail,
                            description: userEmail,
                            discountGroups: null
                        });
                    }
                    return responseMapped;
                })
            );
        } else {
            return of([]);
        }
    }

    private getProductNextSegment(): string | null {
        const segments = this.navigationService.getAllUrlSegments();
        const index = segments.findIndex((s) => s.path === 'product');
        return index !== -1 ? segments[index + 1]?.path ?? null : null;
    }

    private getUrlProductId(): string | null {
        const next = this.getProductNextSegment();
        return next ? next.split(':')[0] : null;
    }

    private assignProductAreaCodes(productAreas: ProductArea[][], productAreaType: ProductAreaType[]): ProductArea[] {
        return productAreas.flatMap((productAreaArray, index) => {
            return productAreaArray?.map((productArea) => ({
                ...productArea,
                code: productAreaType[index]
            }));
        });
    }

    async handleBackClicked(urlSegments: string[]): Promise<void> {
        this.purchaseService.setPurchaseDataLoading(true);

        const isRecentPurchaseFlow = urlSegments.includes('recent');

        if (isRecentPurchaseFlow) {
            this.purchaseService.resetProductsLocalStorage();
            this.shoppingCartService.setCartId(null);
            const currentUrl = this.location.path();
            const currentUrlParts = currentUrl.split('?')[0].split('/');
            const purchaseFlowStepOneUrl = currentUrlParts.slice(0, 4).join('/');
            await this.router.navigateByUrl(purchaseFlowStepOneUrl);
            return;
        }

        const stepFromUrl = this.stepFromUrl$.value;

        if (this.steps.findIndex((s) => s.name === stepFromUrl) === 0) {
            this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
            if (this.isAddOneMoreTicket) {
                this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart']);
            } else {
                this.router.navigate([this.translate.currentLang, 'account', 'tickets']);
            }
            return;
        }

        const prevStep = this.steps[this.steps.findIndex((s) => s.name === stepFromUrl) - 1];

        if (prevStep.name === 'product-area') {
            const nextStepIndexInSegments = urlSegments.indexOf(stepFromUrl);

            urlSegments.splice(nextStepIndexInSegments);
            this.router.navigate([this.translate.currentLang, 'purchase-v2', ...urlSegments]);
            return;
        }

        const prevStepIndexInSegments = urlSegments.indexOf(prevStep.name);
        urlSegments.splice(prevStepIndexInSegments + 1);
        this.router.navigate([this.translate.currentLang, 'purchase-v2', ...urlSegments]);
    }

    private initiateAreas() {
        const areas = this.areas$.value;
        if (!this.steps.some((step) => step.name.includes('areas')) && areas) {
            this.handleSelectArea(areas[0] ?? ({ code: AreaCode.aToB } as Area));
        } else {
            const areasIds = this.purchaseService.getStepDataIdsByStepName('areas');

            if (areasIds?.length > 0 && areas) {
                const area = areas.find((a) => a.id === areasIds[0]);
                if (!area) {
                    return this.handleErrorEvent();
                }
                this.handleSelectArea(area);
            } else {
                this.handleSelectArea(null);
            }
        }
    }

    private transformToSelectAreaData(option: Medium): Area {
        return {
            id: option.id,
            code: option.code as AreaCode | MediumCode,
            name: option.name,
            description: option.description,
            subCategories: null
        };
    }

    private prePurchaseCheckRequired(): boolean {
        const currentActivationModeInUrl = this.selectedActivationMethod;
        return (
            currentActivationModeInUrl !== 'MANUAL' /*( MANUAL = "Later" in UI) */ &&
            this.env.PURCHASE_FLOW.ACTIVATION_CHECK_VALIDITY
        );
    }

    private getPrePurchaseCheckTicketParametersCode(selectedTravellerTicket, selectedToday) {
        const parametersCodeNow = 'PURCHASE'; //means NOW, during purchase

        const activationMethodToCheckWith = selectedToday
            ? parametersCodeNow //when selected day is today hardcode set activation parameter to PURCHASE(as datetime=now) in check to prevent SPECIFIED_ACTIVATION_OUT_OF_BOUNDS_PAST (timestamp) AFC error on check
            : this.selectedActivationMethod;

        const availableActivationParameters =
            selectedTravellerTicket.productParameters?.availableParameters?.activations;
        const pdpParametersCode = selectedTravellerTicket.productParameters.parameterizedDerivedProduct?.parametersCode;
        return !availableActivationParameters &&
            selectedTravellerTicket.activationMethod.length === 1 &&
            pdpParametersCode
            ? pdpParametersCode
            : availableActivationParameters.find((el) => el.activationTrigger === activationMethodToCheckWith)
                  ?.parametersCode;
    }

    private getUrlProductIdWithTickets(localTickets: any[]): string | null {
        if (localTickets?.length) return null;
        const next = this.getProductNextSegment();
        return next ? next.split(':')[0] : null;
    }

    private prePurchaseCheckTickets(callback) {
        if (!this.prePurchaseCheckRequired()) {
            callback();
            return;
        }

        const localTickets = JSON.parse(localStorage.getItem(this.productsLocalStorageKey));

        const activationDateParams = this.purchaseService.getPrePurchaseCheckActivationDateParams(
            this.selectedActivationMethod
        );
        const selectionsInUrl = this.purchaseService.getStepDataIdsByStepName('product');
        const urlProductId = this.getUrlProductIdWithTickets(localTickets);

        // If empty, fetch products from purchaseService based on current Url
        const ticketsToCheck$ = localTickets?.length
            ? of(localTickets)
            : this.purchaseService.getProducts([this.getCurrentAreaValue()]).pipe(
                  map((products) => {
                      const productFromUrl = products.find((p) => p.id === urlProductId);

                      if (!productFromUrl) return [];
                      const extractedParams: { [key: string]: number } = {};
                      selectionsInUrl.forEach((seg: string | number, i: number) => {
                          if (typeof seg === 'string' && seg.startsWith(`${urlProductId}:`)) {
                              const key = seg.split(':')[1];
                              const val = selectionsInUrl[i + 1];
                              if (key && val && !isNaN(Number(val))) {
                                  extractedParams[key] = Number(val);
                              }
                          }
                      });

                      const hasDynamicParams = Object.keys(extractedParams).length > 0;
                      const idx = selectionsInUrl.indexOf(productFromUrl.id);
                      const amount = !hasDynamicParams && idx !== -1 ? selectionsInUrl[idx + 1] || 1 : 1;

                      return [
                          {
                              id: productFromUrl.id,
                              amount: amount,
                              productParameters: productFromUrl.productParameters,
                              activationMethod: productFromUrl.activationMethod,
                              dynamicProductParameters: hasDynamicParams
                                  ? extractedParams
                                  : productFromUrl.dynamicProductParameters
                          }
                      ];
                  })
              );

        ticketsToCheck$.pipe(take(1)).subscribe((tickets) => {
            const ticketsToPrePurchaseCheck = tickets.map((selectedTravellerTicket) => {
                const hasDynamicParams =
                    selectedTravellerTicket.dynamicProductParameters &&
                    Object.keys(selectedTravellerTicket.dynamicProductParameters).length > 0;

                return {
                    productId: selectedTravellerTicket.id,
                    parametersCode: this.getPrePurchaseCheckTicketParametersCode(
                        selectedTravellerTicket,
                        activationDateParams.isToday
                    ),
                    quantity: selectedTravellerTicket.amount,
                    activationDate: this.helperService
                        .appDateTimeToServerDateTime(activationDateParams.date)
                        .toString(),
                    ...(hasDynamicParams && {
                        dynamicProductParameters: selectedTravellerTicket.dynamicProductParameters
                    })
                };
            });

            this.shoppingCartService
                .prePurchaseCheck({
                    tickets: ticketsToPrePurchaseCheck
                })
                .subscribe(async (result) => {
                    const sameValidityFound = !result.checkPassed;
                    if (sameValidityFound) {
                        this.purchaseService.setPurchaseDataLoading(false);
                        const modal = await this.modalCtrl.create({
                            component: TicketPurchaseSameValidityConfirmationComponent,
                            cssClass: 'ticket-purchase-same-validity-confirmation-modal'
                        });

                        await modal.present();

                        const { data: result } = await modal.onWillDismiss<boolean>();
                        if (result === true) {
                            this.purchaseService.setPurchaseDataLoading(true);
                            callback();
                        }
                    } else {
                        callback();
                    }
                });
        });
    }
}
